'use client';
import { useOverlayTrigger } from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';
import PropTypes from 'prop-types';
import React__default, { useRef, useMemo } from 'react';
import { SwanPopoverContextProvider } from './popover.context.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useModeExtractor } from '../../hooks/use-mode-extractor.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { RenderComp } from '../core/render-comp.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';

const propTypes = {
    /**
   * Position in which should the popover be shown
   */ position: PropTypes.oneOf([
        'top',
        'right',
        'bottom',
        'left'
    ])
};
const propKeysToRemove = Object.keys(propTypes);
const Popover = renderWithRef('Popover', propTypes, (props, ref)=>{
    useComponentStylesLoaded('Popover', SWAN_STYLE_KEY_MAP.popover);
    const { position, children } = props;
    const popoverRef = useRef(null);
    const combinedRef = assignRefs(ref, popoverRef);
    const triggerRef = useRef(null);
    const overlayState = useOverlayTriggerState({});
    const modes = useModeExtractor(popoverRef, overlayState.isOpen);
    const { overlayProps, triggerProps } = useOverlayTrigger({
        type: 'dialog'
    }, overlayState, triggerRef);
    const contextValue = useMemo(()=>({
            modes,
            popoverRef,
            triggerRef,
            overlayState,
            overlayProps: overlayProps,
            triggerProps: triggerProps,
            popoverPlacement: position !== null && position !== void 0 ? position : 'top'
        }), [
        triggerRef,
        popoverRef,
        overlayState,
        overlayProps,
        triggerProps,
        position,
        modes
    ]);
    const classNames = [
        'swan-popover-container'
    ];
    return /*#__PURE__*/ React__default.createElement(SwanPopoverContextProvider, {
        value: contextValue
    }, /*#__PURE__*/ React__default.createElement(RenderComp, {
        root: "div",
        forwardedRef: combinedRef,
        classNames: classNames,
        props: props,
        propKeysToRemove: propKeysToRemove
    }, children));
});

export { Popover };
